import React, { useState, useEffect, useMemo, useImperativeHandle } from 'react';
import {
  Box, Chip, Grid, IconButton, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import SectionCard from '../SectionCard';
import InvButton from '../InvButton';
import { InvTextField, InvSelect } from '../InvField';
import StockBalanceAutocomplete from '../StockBalanceAutocomplete';
import SelectPatientDialog from './SelectPatientDialog';
import { createConsumption } from '../../api/dispensing';
import { getAllocatedAdmissions } from '../../../ipd/pages/patient-admission-ipd/api/registrationService';
import {
  issueUomCode, issueUomId, issueQtyIssue, totalIssuableQty, formatIssuableQty,
  batchIssueUomFactor, maxIssueQty,
} from '../../utils/uomConversionEntity';
import { ENCOUNTER_TYPES } from '../../constants/storeManagement';
import { PRIORITY_LEVELS } from '../../constants';
import { generateTxnNumber, formatDate, sanitizeQtyInput } from '../../utils';
import { IPD_COLORS } from '../../theme';

const ACTION_BAR_SX = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 1,
  flexWrap: 'wrap',
  width: { xs: '100%', sm: 'auto' },
  '& > button': { flex: { xs: 1, sm: 'none' }, minWidth: { xs: 0, sm: 'auto' } },
};

const QTY_FIELD_SX = { width: 112, flexShrink: 0, minWidth: 0 };

const TH_SX = { color: IPD_COLORS.textSecondary, whiteSpace: 'nowrap' };

let nextGroupId = 1;

const ENTRY_DEFAULT = {
  rowType: 'entry',
  groupId: '',
  item_key: '',
  item_id: '',
  item_name: '',
  item_code: '',
  uom_id: '',
  batch_number: '',
  grn_item_id: '',
  stock_balance_id: '',
  total_available_quantity: '',
  available_quantity: '',
  mrp: '',
  sale_price: '',
  expiry_date: '',
  requested_quantity: '',
  _catalogue: null,
};

function newEntryRow() {
  return { ...ENTRY_DEFAULT, groupId: `g-${nextGroupId++}` };
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * The batch this screen commits against — the one stock-ledger's FEFO walk
 * reaches first, preferring a batch that carries a restated issue-unit price
 * so the rate and the batch number shown on a row agree.
 *
 * Ordered on fefo_rank rather than trusting the catalogue's array order,
 * because the row now names this batch on screen and on the payload.
 */
function fefoBatch(catalogueItem) {
  const usable = (catalogueItem?.batches || [])
    .filter(b => !b.is_expired && toNumber(b.available_quantity) > 0)
    .sort((a, b) => (toNumber(a.fefo_rank) ?? Number.MAX_SAFE_INTEGER)
      - (toNumber(b.fefo_rank) ?? Number.MAX_SAFE_INTEGER));
  return usable.find(b => toNumber(b.sale_price_issue_uom) || toNumber(b.mrp_issue_uom))
    || usable[0]
    || null;
}

/**
 * Commit an entry as ONE consumption line, in the item's ISSUE unit.
 *
 * This screen used to split the quantity across batches itself, by fefo_rank,
 * in base units. Three problems with that, all now gone:
 *
 *  - It maintained the allocation algorithm twice, in JS and in Python.
 *  - Batches of one item can carry different pack sizes, so a base-unit split
 *    can land a part-pack on a row — 3 strips across a 25-tablet and a
 *    10-tablet batch is 25 + 5, and 5 tablets is half a strip nobody can hand
 *    over.
 *  - The per-batch quantities were never honoured anyway: stock-ledger picks
 *    its own FEFO batches and the request carries no batch identity.
 *
 * So the line submits the typed quantity paired with the issue UOM, exactly
 * like StartDispensePage, and stock-ledger does the one and only allocation.
 */
function buildCommittedRow(entry) {
  const catalogue = entry._catalogue;
  const priced = fefoBatch(catalogue);
  return {
    rowType: 'batch',
    groupId: entry.groupId,
    item_key: entry.item_key || catalogue?.item_id || '',
    item_id: catalogue.item_id,
    item_name: catalogue.item_name || '',
    item_code: catalogue.item_code || '',
    // ISSUE UOM, paired with the quantity the user typed in that same unit.
    //
    // issueUomId, because this row is a STORE CATALOGUE item and the catalogue
    // spells the field issue_uom_id. The item master's own spelling is
    // default_issue_uom_id, so the master-shaped helper found nothing here and
    // fell back to the BASE unit without a word. The line was then typed,
    // priced and totalled in strips but submitted in capsules: 3 STR at ₹50
    // billed ₹150 and took 3 capsules off the shelf instead of 30.
    uom_id: issueUomId(catalogue),
    uom_code: issueUomCode(catalogue),
    // The batch FEFO reaches first. Named on the row and on the payload so the
    // line, the billing charge and the consumption detail all agree on which
    // batch was consumed — stock-ledger still runs its own FEFO walk at posting
    // time, per batch and at each batch's own pack size.
    batch_number: priced?.batch_number ?? '',
    grn_item_id: priced?.grn_item_id ?? '',
    stock_balance_id: priced?.stock_balance_id ?? '',
    total_available_quantity: catalogue.total_available_quantity ?? '',
    available_quantity: catalogue.total_available_quantity ?? '',
    total_issuable_quantity: totalIssuableQty(catalogue?.batches, catalogue),
    // Whole issue units this one batch covers, at its own pack size — past it
    // the posting walk spills into the next batch. See spillsPastBatch.
    batch_issuable_quantity: priced
      ? maxIssueQty(priced.available_quantity, batchIssueUomFactor(priced, catalogue))
      : null,
    // Per ISSUE unit, so quantity x rate is a real line total.
    mrp: priced?.mrp_issue_uom ?? '',
    sale_price: priced?.sale_price_issue_uom ?? priced?.mrp_issue_uom ?? '',
    expiry_date: priced?.expiry_date ?? '',
    requested_quantity: String(entry.requested_quantity),
    _catalogue: catalogue,
  };
}

function entryQuantityError(line) {
  if (line.rowType !== 'entry' || !line.item_id) return undefined;
  if (line.requested_quantity === '' || line.requested_quantity == null) return undefined;
  const catalogue = line._catalogue;
  const message = issueQtyIssue({
    qty: line.requested_quantity,
    // The catalogue's own per-batch factor, not the item's — see
    // batchIssueUomFactor. Capped on total_issuable_quantity rather than
    // total_available_quantity, because floor(sum of tablets / factor)
    // over-promises whenever any batch is left holding a part pack.
    factor: catalogue?.issue_uom_factor ?? 1,
    max: totalIssuableQty(catalogue?.batches, catalogue),
    code: issueUomCode(catalogue),
  });
  return message ? { message } : undefined;
}

function lineSalePrice(line) {
  return toNumber(line.sale_price) || toNumber(line.mrp);
}

function formatPrice(value) {
  const n = toNumber(value);
  if (n == null) return '—';
  return n.toFixed(2);
}

function formatAmount(rate, qty) {
  const r = toNumber(rate);
  const q = toNumber(qty);
  if (r == null || q == null) return '—';
  return (r * q).toFixed(2);
}

/** Quantity and value of one item across every batch FEFO allocated it to. */
function groupTotals(batches) {
  const totals = batches.reduce((acc, line) => {
    const qty = toNumber(line.requested_quantity) || 0;
    acc.qty += qty;
    acc.amount += (lineSalePrice(line) || 0) * qty;
    return acc;
  }, { qty: 0, amount: 0 });
  // Splitting a quantity across batches accumulates float error.
  return { qty: Number(totals.qty.toFixed(6)), amount: totals.amount };
}

/** The group's single rate, or null when its batches were priced differently. */
function groupSaleRate(batches) {
  const rates = batches.map(lineSalePrice);
  const first = rates.length ? rates[0] : null;
  return rates.every(rate => rate === first) ? first : null;
}

function linePayload(line) {
  return {
    item_id: line.item_id,
    // Issue UOM and issue quantity together. stock-ledger converts once, per
    // batch, at each batch's own pack size — the front end never converts.
    uom_id: line.uom_id || undefined,
    // Not an allocation instruction — stock-ledger ignores it and walks FEFO
    // itself. It records which batch the charge and the detail screen name,
    // and it is what lets a batch-tracked item past the service's
    // batch-number-required check.
    batch_number: line.batch_number || undefined,
    expiry_date: line.expiry_date || undefined,
    requested_quantity: Number(line.requested_quantity),
    unit_rate: lineSalePrice(line) ?? 0,
    tax_percent: 0,
    discount_percent: 0,
  };
}

/** True when the quantity outruns the batch on the row, so the posting FEFO
 *  walk finishes it from the next batch — possibly at a different rate. */
function spillsPastBatch(line, qty) {
  const cap = toNumber(line?.batch_issuable_quantity);
  const q = toNumber(qty);
  return cap != null && q != null && q > cap;
}

function entryPreview(line) {
  if (!line?._catalogue) return { rate: null, amount: null };
  const priced = fefoBatch(line._catalogue);
  const rate = priced
    ? (toNumber(priced.sale_price_issue_uom) || toNumber(priced.mrp_issue_uom))
    : null;
  const qty = toNumber(line.requested_quantity);
  return {
    rate,
    amount: rate != null && qty != null ? rate * qty : null,
  };
}

function availLabelFor(line) {
  if (!line?.item_id || !line._catalogue) return '';
  // Whole packs counted per batch at that batch's own size. The item total
  // divided by the item's own factor loses a whole pack the moment one GRN
  // line froze a different one — 50 at 10 plus 32 at 8 is 9 strips, not 8.
  return formatIssuableQty(line._catalogue.batches, line._catalogue, { showLoose: true });
}

function QtyWithUom({
  value, uomCode, error, helperText, disabled, onChange, onBlur, onKeyDown,
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6, minWidth: 0 }}>
      <InvTextField
        value={value}
        label=""
        sx={QTY_FIELD_SX}
        disabled={disabled}
        error={error}
        helperText={helperText}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        inputProps={{
          inputMode: 'numeric',
          'aria-label': 'Quantity',
        }}
      />
      {uomCode ? (
        <Chip
          label={uomCode}
          size="small"
          disabled
          sx={{ height: 24, fontSize: 10.5, fontWeight: 700, mt: 0.4, flexShrink: 0 }}
        />
      ) : null}
    </Box>
  );
}

function CardMeta({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{
        fontSize: 10, fontWeight: 700, color: IPD_COLORS.textSecondary,
        letterSpacing: '0.04em', textTransform: 'uppercase', mb: 0.25,
      }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function RowActions({ onEdit, onDelete, onAdd }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.25 }}>
      {onEdit ? (
        <IconButton
          size="small"
          title="Edit quantity"
          aria-label="Edit quantity"
          onClick={onEdit}
          sx={{ color: IPD_COLORS.primary }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      ) : null}
      <IconButton
        size="small"
        aria-label="Delete row"
        onClick={onDelete}
        sx={{ color: IPD_COLORS.error.text }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
      <Tooltip title="Add row">
        <IconButton
          size="small"
          aria-label="Add row below"
          onClick={onAdd}
          sx={{ color: IPD_COLORS.primary }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function FefoSplitTable({ batches }) {
  return (
    <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, mb: 0.75 }}>
        FEFO split — the earliest-expiry batch is consumed first.
      </Typography>
      <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...TH_SX, fontSize: 11 }}>Batch</TableCell>
            <TableCell sx={{ ...TH_SX, fontSize: 11 }}>Expiry</TableCell>
            <TableCell align="right" sx={{ ...TH_SX, fontSize: 11 }}>Avail. Qty</TableCell>
            <TableCell align="right" sx={{ ...TH_SX, fontSize: 11 }}>Sale Price</TableCell>
            <TableCell align="right" sx={{ ...TH_SX, fontSize: 11 }}>Qty</TableCell>
            <TableCell align="right" sx={{ ...TH_SX, fontSize: 11 }}>Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {batches.map((line, bi) => (
            <TableRow
              key={line.stock_balance_id || `${line.batch_number}-${bi}`}
              sx={bi === 0 ? { backgroundColor: IPD_COLORS.cardBg } : undefined}
            >
              <TableCell sx={{ fontSize: 12, fontWeight: bi === 0 ? 700 : 400 }}>
                {line.batch_number || '—'}
              </TableCell>
              <TableCell sx={{ fontSize: 12 }}>
                {line.expiry_date ? formatDate(line.expiry_date) : '—'}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 12 }}>
                {availLabelFor(line) || '—'}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 12 }}>
                {formatPrice(lineSalePrice(line))}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 12 }}>
                {line.requested_quantity}
                {line.uom_code ? ` ${line.uom_code}` : ''}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 12 }}>
                {formatAmount(lineSalePrice(line), line.requested_quantity)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export default function StoreConsumptionForm({
  storeId,
  storeName,
  consumptionType = 'WARD',
  onCancel,
  onSuccess,
  // Optional controlled patient — when the caller already selects a patient
  // elsewhere (e.g. OT Charge Capture's shared top-of-form picker), pass it
  // here so this form reflects that same patient instead of running its own
  // independent search. Omit (undefined) to keep the original behavior.
  patient,
  hideSubmit = false,
  formApiRef,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isPatient = consumptionType === 'PATIENT';
  const isControlledPatient = patient !== undefined;
  const [saving, setSaving] = useState(false);
  const [lines, setLines] = useState([newEntryRow()]);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(patient || null);
  const [admissions, setAdmissions] = useState([]);
  const [admissionsLoading, setAdmissionsLoading] = useState(false);
  const [admissionsError, setAdmissionsError] = useState(null);

  const { control, handleSubmit, formState: { errors }, getValues } = useForm({
    defaultValues: {
      transaction_number: generateTxnNumber('CON'),
      transaction_date: dayjs().format('YYYY-MM-DD'),
      priority: 'NORMAL',
      remarks: '',
      encounter_type: 'IP',
    },
  });

  // Controlled mode: reflect the caller's patient whenever it changes.
  useEffect(() => {
    if (isControlledPatient) setSelectedPatient(patient || null);
  }, [isControlledPatient, patient]);

  // Item stock is store-specific — wipe lines when the store changes.
  useEffect(() => {
    setLines([newEntryRow()]);
  }, [storeId]);

  // Load allocated IPD admissions once when opening Patient Consumption —
  // only needed for the uncontrolled (own search) flow.
  useEffect(() => {
    if (!isPatient || isControlledPatient) return undefined;
    let cancelled = false;
    setAdmissionsLoading(true);
    setAdmissionsError(null);
    getAllocatedAdmissions()
      .then((list) => {
        if (!cancelled) setAdmissions(list);
      })
      .catch((e) => {
        if (!cancelled) {
          setAdmissions([]);
          setAdmissionsError(e.message || 'Failed to load allocated patients');
        }
      })
      .finally(() => {
        if (!cancelled) setAdmissionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isPatient, isControlledPatient]);

  /**
   * One render row per item, not per allocated batch.
   *
   * `lines` stays flat (entry + batch rows keyed by groupId) because FEFO
   * allocation and the submit payload are built from it. Only the table is
   * grouped, so an item that draws on three batches reads as one line with a
   * breakdown rather than three look-alike rows.
   */
  const groupRows = useMemo(() => {
    const order = [];
    const byId = new Map();
    lines.forEach((line, index) => {
      let group = byId.get(line.groupId);
      if (!group) {
        group = { groupId: line.groupId, entry: null, entryIndex: -1, batches: [], lastIndex: index };
        byId.set(line.groupId, group);
        order.push(group);
      }
      group.lastIndex = index;
      if (line.rowType === 'entry') {
        group.entry = line;
        group.entryIndex = index;
      } else {
        group.batches.push(line);
      }
    });
    return order;
  }, [lines]);

  function toggleGroup(groupId) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }

  function insertRowAfter(index) {
    setLines(prev => [
      ...prev.slice(0, index + 1),
      newEntryRow(),
      ...prev.slice(index + 1),
    ]);
  }

  function applySelectedPatient(patient) {
    setSelectedPatient(patient);
  }

  function clearSelectedPatient() {
    setSelectedPatient(null);
  }

  function selectCatalogueItem(index, itemKey, catalogueItem) {
    setLines(prev => {
      const line = prev[index];
      if (!line || line.rowType !== 'entry') return prev;

      if (!catalogueItem) {
        return prev.map((row, i) => (i === index ? { ...newEntryRow(), groupId: line.groupId } : row));
      }

      const nextEntry = {
        ...line,
        rowType: 'entry',
        item_key: itemKey || catalogueItem.item_id,
        item_id: catalogueItem.item_id,
        item_name: catalogueItem.item_name || '',
        item_code: catalogueItem.item_code || '',
        // The entry row is what the user types in, so it carries the ISSUE
        // unit. The batch rows it expands into carry the base one.
        uom_id: issueUomId(catalogueItem),
        uom_code: issueUomCode(catalogueItem),
        batch_number: '',
        grn_item_id: '',
        stock_balance_id: '',
        total_available_quantity: catalogueItem.total_available_quantity ?? '',
        available_quantity: catalogueItem.total_available_quantity ?? '',
        requested_quantity: line.requested_quantity || '',
        _catalogue: catalogueItem,
      };

      // Keep as editable entry until quantity blur — don't auto-expand on item select alone.
      return prev.map((row, i) => (i === index ? nextEntry : row));
    });
  }

  function updateEntryQuantity(index, value) {
    setLines(prev => prev.map((row, i) => (
      i === index && row.rowType === 'entry'
        ? { ...row, requested_quantity: value }
        : row
    )));
  }

  function commitEntryLine(index) {
    setLines(prev => {
      const line = prev[index];
      if (!line || line.rowType !== 'entry' || !line._catalogue) return prev;
      if (entryQuantityError(line)) return prev;
      if (!(toNumber(line.requested_quantity) > 0)) return prev;

      return prev.map((row, i) => (i === index ? buildCommittedRow(line) : row));
    });
  }

  function reopenGroupAsEntry(groupId) {
    setLines(prev => {
      const groupRows = prev.filter(l => l.groupId === groupId);
      if (!groupRows.length) return prev;
      const head = groupRows[0];
      const totalRequested = groupRows.reduce((sum, row) => sum + (toNumber(row.requested_quantity) || 0), 0);
      const entry = {
        ...ENTRY_DEFAULT,
        groupId,
        item_key: head.item_key || head.item_id,
        item_id: head.item_id,
        item_name: head.item_name,
        item_code: head.item_code,
        // Back to the entry's own unit — the batch rows were in base.
        uom_id: issueUomId(head._catalogue),
        uom_code: issueUomCode(head._catalogue),
        total_available_quantity: head.total_available_quantity,
        available_quantity: head.total_available_quantity,
        // The committed row is already in the issue unit the entry was typed
        // in, so reopening it is a straight read — no dividing a split total
        // back into fractions.
        requested_quantity: String(totalRequested || ''),
        _catalogue: head._catalogue,
      };
      const start = prev.findIndex(l => l.groupId === groupId);
      const withoutGroup = prev.filter(l => l.groupId !== groupId);
      return [
        ...withoutGroup.slice(0, start),
        entry,
        ...withoutGroup.slice(start),
      ];
    });
  }

  function removeGroup(groupId) {
    setLines(prev => {
      const next = prev.filter(l => l.groupId !== groupId);
      return next.length ? next : [newEntryRow()];
    });
  }

  async function onSubmit(data) {
    const pendingEntries = lines.filter(l => l.rowType === 'entry' && l.item_id && toNumber(l.requested_quantity) > 0);
    if (pendingEntries.some(l => entryQuantityError(l))) {
      toast.error('Requested quantity cannot exceed available stock');
      return;
    }
    if (pendingEntries.length) {
      toast.error('Press Enter or click away to confirm each item quantity');
      return;
    }

    const validLines = lines.filter(l => l.rowType === 'batch' && l.item_id && Number(l.requested_quantity) > 0);
    if (!validLines.length) {
      toast.error('Add at least one item with quantity');
      return;
    }
    if (isPatient && !selectedPatient?.patient_id) {
      toast.error('Select a patient for patient consumption');
      return;
    }

    setSaving(true);
    try {
      const body = {
        transaction_number: data.transaction_number,
        from_store_id: storeId,
        consumption_type: consumptionType,
        transaction_date: data.transaction_date || undefined,
        priority: data.priority || 'NORMAL',
        remarks: data.remarks || undefined,
        items: validLines.map(linePayload),
      };

      if (isPatient) {
        body.patient_id = selectedPatient.patient_id;
        body.encounter_type = data.encounter_type || undefined;
        if (selectedPatient.admission_id) body.admission_id = selectedPatient.admission_id;
        if (selectedPatient.billing_account_id) body.billing_account_id = selectedPatient.billing_account_id;
      }

      // API: POST /inventory/dispensing/consumption/
      await createConsumption(body);
      toast.success(`${isPatient ? 'Patient' : 'Ward'} consumption created`);
      onSuccess?.();
    } catch (e) {
      toast.error(e.message || 'Failed to create consumption');
    } finally {
      setSaving(false);
    }
  }

  const encounterOptions = ENCOUNTER_TYPES.map(v => ({ value: v, label: v }));
  const priorityOptions = PRIORITY_LEVELS.map(v => ({ value: v, label: v }));
  const hasEntryErrors = lines.some(l => Boolean(entryQuantityError(l)));
  const hasBatchItems = lines.some(l => l.rowType === 'batch' && l.item_id && Number(l.requested_quantity) > 0);
  const canSubmit = !hasEntryErrors
    && hasBatchItems
    && (!isPatient || Boolean(selectedPatient?.patient_id));

  function collectConsumption() {
    const pendingEntries = lines.filter(l => l.rowType === 'entry' && l.item_id && toNumber(l.requested_quantity) > 0);
    if (pendingEntries.some(l => entryQuantityError(l))) {
      return { error: 'Requested quantity cannot exceed available stock' };
    }
    if (pendingEntries.length) {
      return { error: 'Press Enter or click away to confirm each item quantity' };
    }
    const validLines = lines.filter(l => l.rowType === 'batch' && l.item_id && Number(l.requested_quantity) > 0);
    if (!validLines.length) return { skipped: true };
    if (!storeId) return { error: 'Select a store for patient consumption' };
    if (isPatient && !selectedPatient?.patient_id) {
      return { error: 'Select a patient for patient consumption' };
    }
    const data = getValues();
    const body = {
      transaction_number: data.transaction_number,
      from_store_id: storeId,
      consumption_type: consumptionType,
      transaction_date: data.transaction_date || undefined,
      priority: data.priority || 'NORMAL',
      remarks: data.remarks || undefined,
      items: validLines.map(linePayload),
    };
    if (isPatient) {
      body.patient_id = selectedPatient.patient_id;
      body.encounter_type = data.encounter_type || undefined;
      if (selectedPatient.admission_id) body.admission_id = selectedPatient.admission_id;
      if (selectedPatient.billing_account_id) body.billing_account_id = selectedPatient.billing_account_id;
    }
    return { body, lines: validLines };
  }

  useImperativeHandle(formApiRef, () => ({
    collect: collectConsumption,
    reset() {
      setLines([newEntryRow()]);
    },
  }), [
    lines, storeId, consumptionType, selectedPatient, isPatient, getValues,
  ]);

  const actionButtons = (
    <>
      <InvButton variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={onCancel}>
        Back to list
      </InvButton>
      {!hideSubmit && (
        <InvButton type="submit" size="small" loading={saving} disabled={!canSubmit}>
          Create consumption
        </InvButton>
      )}
    </>
  );

  return (
    <Box
      component="form"
      onSubmit={hideSubmit ? (e) => e.preventDefault() : handleSubmit(onSubmit)}
      sx={{ pb: isMobile && !hideSubmit ? 10 : 0, minWidth: 0 }}
    >
      {!hideSubmit && (
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 2, gap: 1, flexWrap: 'wrap',
        }}>
          <Box sx={{ minWidth: 0, flex: '1 1 180px' }}>
            <Typography variant="overline" sx={{ color: IPD_COLORS.textSecondary, letterSpacing: 1 }}>
              {isPatient ? 'Patient Consumption' : 'Ward Consumption'}
            </Typography>
            <Typography sx={{ fontWeight: 600, color: IPD_COLORS.textPrimary }}>
              New consumption{storeName ? ` · ${storeName}` : ''}
            </Typography>
          </Box>
          {!isMobile && (
            <Box sx={ACTION_BAR_SX}>
              {actionButtons}
            </Box>
          )}
        </Box>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <SectionCard title="Consumption details">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Controller
                  name="transaction_number"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <InvTextField {...field} label="Consumption # *" error={errors.transaction_number} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Controller
                  name="transaction_date"
                  control={control}
                  render={({ field }) => (
                    <InvTextField {...field} label="Date" type="date" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <InvSelect label="Priority" value={field.value} onChange={field.onChange} options={priorityOptions} />
                  )}
                />
              </Grid>
              {isPatient && isControlledPatient && (
                <Grid item xs={12} sm={6} md={4}>
                  <Controller
                    name="encounter_type"
                    control={control}
                    render={({ field }) => (
                      <InvSelect
                        label="Encounter type"
                        value={field.value}
                        onChange={field.onChange}
                        options={encounterOptions}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Controller
                  name="remarks"
                  control={control}
                  render={({ field }) => <InvTextField {...field} label="Remarks" multiline rows={2} />}
                />
              </Grid>
            </Grid>
          </SectionCard>

          {isPatient && !isControlledPatient && (
            <SectionCard
              title="Patient"
              sx={{ mt: 2.5 }}
              actions={!isControlledPatient ? (
                <InvButton
                  size="small"
                  startIcon={<PersonSearchIcon />}
                  onClick={() => setSearchOpen(true)}
                >
                  {selectedPatient ? 'Change patient' : 'Search patient'}
                </InvButton>
              ) : undefined}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  {selectedPatient ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        flexWrap: 'wrap',
                        minWidth: 0,
                        px: 1.5,
                        py: 1.25,
                        border: `1px solid ${IPD_COLORS.border}`,
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: IPD_COLORS.textPrimary, fontWeight: 600, fontSize: 14 }}>
                          {selectedPatient.name || '—'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: IPD_COLORS.textSecondary }}>
                          UHID: {selectedPatient.uhid || '—'}
                          {' · '}
                          Gender: {selectedPatient.gender || '—'}
                          {' · '}
                          Age: {selectedPatient.age ? `${selectedPatient.age} yrs` : '—'}
                        </Typography>
                      </Box>
                      {!isControlledPatient && (
                        <IconButton
                          size="small"
                          aria-label="Clear patient"
                          onClick={clearSelectedPatient}
                          sx={{ color: IPD_COLORS.textSecondary }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        border: `1px dashed ${IPD_COLORS.border}`,
                        borderRadius: 1,
                      }}
                    >
                      <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 14 }}>
                        {isControlledPatient ? 'Select a patient above to continue' : 'Search and select a patient to continue'}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="encounter_type"
                    control={control}
                    render={({ field }) => (
                      <InvSelect
                        label="Encounter type"
                        value={field.value}
                        onChange={field.onChange}
                        options={encounterOptions}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </SectionCard>
          )}

          <SectionCard title="Items" sx={{ mt: 2.5 }}>
            <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary, display: 'block', mb: 1.5 }}>
              Quantity is entered in the item&apos;s issue unit. Batches are picked by FEFO
              (earliest expiry first) when the consumption posts — each at its own pack size,
              so only whole packs are ever issued.
            </Typography>
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {groupRows.map((group) => {
                  if (group.entry) {
                    const line = group.entry;
                    const entryIndex = group.entryIndex;
                    const qtyError = entryQuantityError(line);
                    const preview = entryPreview(line);
                    return (
                      <Box
                        key={`${group.groupId}-entry`}
                        sx={{
                          border: `1px solid ${IPD_COLORS.border}`,
                          borderRadius: '8px',
                          p: 1.25,
                          backgroundColor: IPD_COLORS.cardBg,
                        }}
                      >
                        <StockBalanceAutocomplete
                          storeId={storeId}
                          value={line.item_key}
                          onChange={(key, item) => selectCatalogueItem(entryIndex, key, item)}
                          placeholder="Search item (min 2 chars)…"
                        />
                        <Box sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 1,
                          mt: 1.25,
                        }}>
                          <CardMeta label="Batch">
                            <Typography variant="body2" sx={{ color: IPD_COLORS.textSecondary }}>
                              Auto (FEFO)
                            </Typography>
                          </CardMeta>
                          <CardMeta label="Avail. Qty">
                            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                              {availLabelFor(line) || '—'}
                            </Typography>
                          </CardMeta>
                          <Box sx={{ gridColumn: '1 / -1' }}>
                            <CardMeta label="Quantity *">
                              <QtyWithUom
                                value={line.requested_quantity}
                                uomCode={line.uom_code}
                                error={qtyError}
                                helperText={qtyError?.message}
                                onChange={(e) => updateEntryQuantity(
                                  entryIndex, sanitizeQtyInput(e.target.value),
                                )}
                                onBlur={() => commitEntryLine(entryIndex)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    commitEntryLine(entryIndex);
                                  }
                                }}
                              />
                            </CardMeta>
                          </Box>
                          <CardMeta label="Sale Price">
                            <Typography variant="body2">
                              {preview.rate == null ? '—' : formatPrice(preview.rate)}
                            </Typography>
                          </CardMeta>
                          <CardMeta label="Amount">
                            <Typography variant="body2">
                              {preview.amount == null ? '—' : preview.amount.toFixed(2)}
                            </Typography>
                          </CardMeta>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                          <RowActions
                            onDelete={() => removeGroup(line.groupId)}
                            onAdd={() => insertRowAfter(group.lastIndex)}
                          />
                        </Box>
                      </Box>
                    );
                  }

                  const head = group.batches[0];
                  const totals = groupTotals(group.batches);
                  const rate = groupSaleRate(group.batches);
                  const extraBatches = group.batches.length - 1;
                  const expanded = expandedGroups.has(group.groupId);

                  return (
                    <Box
                      key={group.groupId}
                      sx={{
                        border: `1px solid ${IPD_COLORS.border}`,
                        borderRadius: '8px',
                        p: 1.25,
                        backgroundColor: IPD_COLORS.cardBg,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: IPD_COLORS.textPrimary, minWidth: 0 }}>
                          {head.item_name || '—'}
                          {head.item_code ? (
                            <Typography component="span" sx={{ color: IPD_COLORS.textSecondary, fontSize: 12 }}>
                              {` (${head.item_code})`}
                            </Typography>
                          ) : null}
                        </Typography>
                        <RowActions
                          onEdit={() => reopenGroupAsEntry(group.groupId)}
                          onDelete={() => removeGroup(group.groupId)}
                          onAdd={() => insertRowAfter(group.lastIndex)}
                        />
                      </Box>
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 1,
                        mt: 1.25,
                      }}>
                        <CardMeta label="Batch">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: head.batch_number ? IPD_COLORS.textPrimary : IPD_COLORS.textSecondary,
                                fontWeight: head.batch_number ? 600 : 400,
                              }}
                            >
                              {head.batch_number || 'Auto (FEFO)'}
                            </Typography>
                            {extraBatches > 0 && (
                              <Tooltip title={expanded ? 'Hide batch split' : `Split across ${group.batches.length} batches`}>
                                <IconButton
                                  size="small"
                                  aria-label="Toggle batch split"
                                  onClick={() => toggleGroup(group.groupId)}
                                  sx={{ color: IPD_COLORS.primary }}
                                >
                                  {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          {extraBatches > 0 && (
                            <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, mt: 0.25 }}>
                              {`+${extraBatches} more batch${extraBatches > 1 ? 'es' : ''}`}
                            </Typography>
                          )}
                          {spillsPastBatch(head, totals.qty) && (
                            <Typography sx={{ fontSize: 11, color: IPD_COLORS.warning.text, mt: 0.25 }}>
                              Spills to the next batch (FEFO)
                            </Typography>
                          )}
                        </CardMeta>
                        <CardMeta label="Avail. Qty">
                          <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                            {availLabelFor(head) || '—'}
                          </Typography>
                        </CardMeta>
                        <Box sx={{ gridColumn: '1 / -1' }}>
                          <CardMeta label="Quantity">
                            <QtyWithUom
                              value={String(totals.qty)}
                              uomCode={head.uom_code}
                              disabled
                            />
                          </CardMeta>
                        </Box>
                        <CardMeta label="Sale Price">
                          <Typography variant="body2">
                            {rate == null ? 'Mixed' : formatPrice(rate)}
                          </Typography>
                        </CardMeta>
                        <CardMeta label="Amount">
                          <Typography variant="body2">{totals.amount.toFixed(2)}</Typography>
                        </CardMeta>
                        <CardMeta label="Expiry">
                          <Typography variant="body2">
                            {head.expiry_date ? formatDate(head.expiry_date) : '—'}
                          </Typography>
                        </CardMeta>
                      </Box>
                      {extraBatches > 0 && expanded && (
                        <Box sx={{ mt: 1.25, backgroundColor: IPD_COLORS.panelHeaderBg, borderRadius: 1, p: 1 }}>
                          <FefoSplitTable batches={group.batches} />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
                <Table
                  size="small"
                  sx={{
                    minWidth: 960,
                    '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...TH_SX, minWidth: 220 }}>Item</TableCell>
                      <TableCell sx={{ ...TH_SX, width: 120 }}>Batch</TableCell>
                      <TableCell sx={{ ...TH_SX, width: 90 }}>Sale Price</TableCell>
                      <TableCell sx={{ ...TH_SX, width: 110 }}>Avail. Qty</TableCell>
                      <TableCell sx={{ ...TH_SX, minWidth: 180, width: 180 }}>Quantity *</TableCell>
                      <TableCell sx={{ ...TH_SX, width: 100 }}>Amount</TableCell>
                      <TableCell sx={{ ...TH_SX, width: 110 }}>Expiry</TableCell>
                      <TableCell sx={{ width: 110 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupRows.map((group) => {
                      if (group.entry) {
                        const line = group.entry;
                        const entryIndex = group.entryIndex;
                        const qtyError = entryQuantityError(line);
                        const preview = entryPreview(line);
                        const availLabel = availLabelFor(line);
                        return (
                          <TableRow key={`${group.groupId}-entry`}>
                            <TableCell>
                              <StockBalanceAutocomplete
                                storeId={storeId}
                                value={line.item_key}
                                onChange={(key, item) => selectCatalogueItem(entryIndex, key, item)}
                                placeholder="Search item (min 2 chars)…"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: IPD_COLORS.textSecondary }}>
                                Auto (FEFO)
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: IPD_COLORS.textPrimary }}>
                                {preview.rate == null ? '—' : formatPrice(preview.rate)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {availLabel ? (
                                <Typography variant="body2" sx={{ color: IPD_COLORS.textPrimary }}>
                                  {availLabel}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <QtyWithUom
                                value={line.requested_quantity}
                                uomCode={line.uom_code}
                                error={qtyError}
                                helperText={qtyError?.message}
                                onChange={(e) => updateEntryQuantity(
                                  entryIndex, sanitizeQtyInput(e.target.value),
                                )}
                                onBlur={() => commitEntryLine(entryIndex)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    commitEntryLine(entryIndex);
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: IPD_COLORS.textPrimary }}>
                                {preview.amount == null ? '—' : preview.amount.toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: IPD_COLORS.textSecondary }}>
                                —
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <RowActions
                                onDelete={() => removeGroup(line.groupId)}
                                onAdd={() => insertRowAfter(group.lastIndex)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      }

                      const head = group.batches[0];
                      const totals = groupTotals(group.batches);
                      const rate = groupSaleRate(group.batches);
                      const extraBatches = group.batches.length - 1;
                      const expanded = expandedGroups.has(group.groupId);

                      return (
                        <React.Fragment key={group.groupId}>
                          <TableRow>
                            <TableCell>
                              <Typography sx={{ fontSize: 14, color: IPD_COLORS.textPrimary }}>
                                {head.item_name || '—'}
                                {head.item_code ? (
                                  <Typography component="span" sx={{ color: IPD_COLORS.textSecondary, fontSize: 12 }}>
                                    {` (${head.item_code})`}
                                  </Typography>
                                ) : null}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: head.batch_number ? IPD_COLORS.textPrimary : IPD_COLORS.textSecondary,
                                    fontWeight: head.batch_number ? 600 : 400,
                                  }}
                                >
                                  {head.batch_number || 'Auto (FEFO)'}
                                </Typography>
                                {extraBatches > 0 && (
                                  <Tooltip title={expanded ? 'Hide batch split' : `Split across ${group.batches.length} batches`}>
                                    <IconButton
                                      size="small"
                                      aria-label="Toggle batch split"
                                      onClick={() => toggleGroup(group.groupId)}
                                      sx={{ color: IPD_COLORS.primary }}
                                    >
                                      {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                              {extraBatches > 0 && (
                                <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, mt: 0.25 }}>
                                  {`+${extraBatches} more batch${extraBatches > 1 ? 'es' : ''}`}
                                </Typography>
                              )}
                              {spillsPastBatch(head, totals.qty) && (
                                <Typography sx={{ fontSize: 11, color: IPD_COLORS.warning.text, mt: 0.25 }}>
                                  Spills to the next batch (FEFO)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: IPD_COLORS.textPrimary }}>
                                {rate == null ? 'Mixed' : formatPrice(rate)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: IPD_COLORS.textPrimary }}>
                                {availLabelFor(head) || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <QtyWithUom
                                value={String(totals.qty)}
                                uomCode={head.uom_code}
                                disabled
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: IPD_COLORS.textPrimary }}>
                                {totals.amount.toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: IPD_COLORS.textPrimary }}>
                                {head.expiry_date ? formatDate(head.expiry_date) : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <RowActions
                                onEdit={() => reopenGroupAsEntry(group.groupId)}
                                onDelete={() => removeGroup(group.groupId)}
                                onAdd={() => insertRowAfter(group.lastIndex)}
                              />
                            </TableCell>
                          </TableRow>
                          {extraBatches > 0 && expanded && (
                            <TableRow>
                              <TableCell colSpan={8} sx={{ backgroundColor: IPD_COLORS.panelHeaderBg, py: 1 }}>
                                <FefoSplitTable batches={group.batches} />
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </SectionCard>

          {!hideSubmit && !isMobile && (
            <Box sx={{ ...ACTION_BAR_SX, mt: 2.5, ml: 'auto' }}>
              {actionButtons}
            </Box>
          )}
        </Grid>
      </Grid>

      {isMobile && !hideSubmit && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 20,
            p: 1.5,
            backgroundColor: IPD_COLORS.cardBg,
            borderTop: `1px solid ${IPD_COLORS.border}`,
            boxShadow: '0 -4px 16px rgba(13,35,63,0.08)',
          }}
        >
          <Box sx={ACTION_BAR_SX}>{actionButtons}</Box>
        </Box>
      )}

      {isPatient && !isControlledPatient && (
        <SelectPatientDialog
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onSelect={applySelectedPatient}
          admissions={admissions}
          loading={admissionsLoading}
          error={admissionsError}
        />
      )}
    </Box>
  );
}
